import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  Box,
  Alert,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { SERVICE_DEFAULTS } from '../../utils/constants';
import { serviceSchema } from '../../schemas/validationSchemas';

/**
 * Formulário de Serviço (Create/Update) com validação Zod
 * 
 * @param {boolean} open - Se o dialog está aberto
 * @param {Function} onClose - Callback ao fechar
 * @param {Function} onSubmit - Callback ao submeter (recebe formData)
 * @param {Object} data - Dados do serviço para edição (null para criação)
 * @param {boolean} loading - Estado de loading do submit
 */
export default function ServiceForm({ open, onClose, onSubmit, data, loading = false, serverError = null }) {
  const isEdit = !!data;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(serviceSchema),
    mode: 'onChange', // Validação em tempo real
    defaultValues: {
      name: '',
      displayName: '',
      description: '',
      category: '',
      duration: SERVICE_DEFAULTS.DURATION_MINUTES,
      price: 0,
    },
  });

  // Preenche form quando data muda
  useEffect(() => {
    if (open) {
      if (data) {
        reset({
          name: data.name || '',
          displayName: data.displayName || '',
          description: data.description || '',
          category: data.category || '',
          duration: data.defaultDurationMinutes || SERVICE_DEFAULTS.DURATION_MINUTES,
          price: data.defaultPrice || 0,
        });
      } else {
        reset({
          name: '',
          description: '',
          category: '',
          duration: SERVICE_DEFAULTS.DURATION_MINUTES,
          price: 0,
        });
      }
    }
  }, [data, open, reset]);

  // Erro que veio do servidor apontando um campo (corpo da resposta: { message, field }).
  //
  // Por que no campo e não só no toast: o toast some sozinho. Medido em PRD 25/08 — um dono
  // ficou repetindo o mesmo POST com o nome 'HISTEROSCOPIA' já usado, recebendo 400 a cada
  // tentativa. A mensagem existia; ela só não estava onde ele estava olhando.
  useEffect(() => {
    if (serverError?.field && serverError?.message) {
      setError(serverError.field, { type: 'server', message: serverError.message });
    }
  }, [serverError, setError]);

  const onSubmitForm = (formData) => {
    // Transforma de volta para o formato da API
    const apiData = {
      name: formData.name,
      description: formData.description || '',
      category: formData.category || '',
      defaultDurationMinutes: Number(formData.duration),
      defaultPrice: Number(formData.price),
    };
    onSubmit(apiData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Editar Serviço' : 'Novo Serviço'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Nome */}
            <TextField
              autoFocus
              label="Nome do Serviço (Interno)"
              type="text"
              fullWidth
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message || 'Nome técnico usado internamente'}
              required
              disabled={loading}
            />

            {/* Nome de Exibição */}
            <TextField
              label="Nome de Exibição (Público)"
              type="text"
              fullWidth
              {...register('displayName')}
              error={!!errors.displayName}
              helperText={errors.displayName?.message || 'Nome mostrado ao público. Se vazio, usa o nome interno'}
              disabled={loading}
            />

            {/* Descrição */}
            <TextField
              label="Descrição"
              type="text"
              fullWidth
              multiline
              rows={3}
              {...register('description')}
              error={!!errors.description}
              helperText={errors.description?.message}
              disabled={loading}
            />

            {/* Categoria */}
            <TextField
              label="Categoria"
              type="text"
              fullWidth
              {...register('category')}
              error={!!errors.category}
              helperText={errors.category?.message || 'Ex: Saúde, Beleza, Educação, Tecnologia, etc.'}
              disabled={loading}
            />

            {/* Duração */}
            <TextField
              label="Duração Padrão (minutos)"
              type="number"
              fullWidth
              {...register('duration', { valueAsNumber: true })}
              error={!!errors.duration}
              helperText={errors.duration?.message}
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <TimeIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Preço */}
            <TextField
              label="Preço Padrão"
              type="number"
              fullWidth
              {...register('price', { valueAsNumber: true })}
              error={!!errors.price}
              helperText={errors.price?.message}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon />
                    R$
                  </InputAdornment>
                ),
                inputProps: {
                  step: '0.01',
                  min: '0',
                },
              }}
            />

            {/* Alert de validação */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                Por favor, corrija os erros acima antes de salvar.
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !isValid || (!isDirty && !isEdit)}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
